import { divIcon, type LatLngExpression, type LeafletMouseEvent } from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { useEffect } from "react";
import { formatStartsAt } from "../lib/format";

/**
 * Minimal structural shape the map needs to plot a pin. Both PublicEventCard
 * (landing/map page) and EventCard (events list) satisfy it, so the same map
 * component serves both without coupling to either's full type.
 */
export type MapEvent = {
  id: string;
  title: string;
  starts_at: string;
  venue_name: string;
  format: string | null;
  going_count: number;
  lat: number | null;
  lng: number | null;
};

const HAIFA_CENTER: LatLngExpression = [32.8156, 34.9892];

// Brand-tinted pin per event format. Defaults to purple if format unknown.
const FORMAT_COLOR: Record<string, string> = {
  cafe: "#F59E0B",
  run: "#2563EB",
  boardgames: "#7C3AED",
  movie: "#EF4444",
  other: "#059669",
};

function pinIcon(color: string) {
  // Classic teardrop pin in brand color, navy outline, white dot.
  const svg = `
    <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 1 C 7 1 1 8 1 16 c 0 11 15 25 15 25 s 15 -14 15 -25 c 0 -8 -6 -15 -15 -15 z"
            fill="${color}" stroke="#2B2F54" stroke-width="2" />
      <circle cx="16" cy="16" r="5" fill="#FFFFFF" />
    </svg>`;
  return divIcon({
    html: svg,
    className: "nf-pin",
    iconSize: [32, 42],
    iconAnchor: [16, 40],
    popupAnchor: [0, -36],
  });
}

type Props = {
  events: MapEvent[];
  height?: number | string;
  /** Selected event id — when set, pan to that pin. */
  focusedId?: string | null;
  onMarkerClick?: (id: string) => void;
  onPopupCta?: (id: string) => void;
  /** Fired when the user taps empty map (not a pin) — used to deselect. */
  onMapClick?: () => void;
};

export function EventsMap({ events, height = 420, focusedId, onMarkerClick, onPopupCta, onMapClick }: Props) {
  const withCoords = events.filter((e) => e.lat != null && e.lng != null);

  return (
    <div className="rounded-[var(--radius-md)] overflow-hidden border border-ink-100" style={{ height }}>
      <MapContainer
        center={HAIFA_CENTER}
        zoom={13}
        scrollWheelZoom
        style={{ width: "100%", height: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FocusController events={withCoords} focusedId={focusedId ?? null} />
        {onMapClick && <DeselectOnClick onClick={onMapClick} />}
        {withCoords.map((e) => (
          <Marker
            key={e.id}
            position={[e.lat as number, e.lng as number]}
            icon={pinIcon(FORMAT_COLOR[e.format ?? "other"] ?? "#7C3AED")}
            eventHandlers={{
              click: () => onMarkerClick?.(e.id),
            }}
          >
            <Popup>
              <div className="text-[14px]">
                <div className="font-bold text-ink-900 mb-1">{e.title}</div>
                <div className="text-ink-500">{formatStartsAt(e.starts_at)}</div>
                <div className="text-ink-500">{e.venue_name}</div>
                <div className="text-ink-700 mt-1">
                  {e.going_count} {plural(e.going_count, ["идёт", "идут", "идут"])}
                </div>
                <button
                  type="button"
                  onClick={() => onPopupCta?.(e.id)}
                  className="mt-2 inline-block text-brand-purple font-semibold"
                >
                  Подробнее →
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

/** Clicking empty map (a pin click doesn't propagate here) clears selection. */
function DeselectOnClick({ onClick }: { onClick: () => void }) {
  useMapEvents({
    click(_e: LeafletMouseEvent) {
      onClick();
    },
  });
  return null;
}

function FocusController({
  events,
  focusedId,
}: {
  events: MapEvent[];
  focusedId: string | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (!focusedId) return;
    const target = events.find((e) => e.id === focusedId);
    if (!target || target.lat == null || target.lng == null) return;
    map.flyTo([target.lat as number, target.lng as number], 15, { duration: 0.6 });
  }, [focusedId, events, map]);
  return null;
}

function plural(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}
